import * as React from "react";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { Loader } from "@/components/ui/loader";

import { SnapshotCollectionsOutcomesSearchInput } from "./input";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { SnapshotCollectionsOutcomesSearchThinking } from "./thinking";
import { SnapshotCollectionsOutcomesSearchResults } from "./results";
import { motion } from "framer-motion";
import { SnapshotSearchDocument } from "./types";
import { SnapshotCollectionsOutcomesSearchWebsocket } from "./websocket";
import { SnapshotCollectionsOutcomesSearchDocuments } from "./documents";
import { useSearchBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { useSearchParams } from "react-router";

interface Props {
  selectedCollection: SnapshotCollection;
}

export const SnapshotCollectionsOutcomesSearch: React.FC<Props> = ({ selectedCollection }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchId = searchParams.get("searchId") || "";
  const [showThinkingType, setShowThinkingType] = React.useState<boolean>(false);
  const query = searchParams.get("query") || "";
  const setSearchId = (value: string) => {
    setSearchParams(prev => {
      prev.set("searchId", value);
      return prev;
    });
  };
  const setQuery = (value: string) => {
    setSearchParams(prev => {
      prev.set("query", value);
      return prev;
    });
  };

  const [thinking, setThinking] = React.useState<string>("");
  const [isThinkingComplete, setIsThinkingComplete] = React.useState<boolean>(false);
  const [documents, setDocuments] = React.useState<SnapshotSearchDocument[] | undefined>(undefined);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const [snapshotCollectionOutcomes, setSnapshotCollectionOutcomes] = React.useState<
    BusinessReportOutcome[] | undefined
  >(undefined);

  useSearchBusinessReportsOutcomeQuery(
    {
      business_report_id: selectedCollection.business_report_id,
      snapshot_collection_id: selectedCollection.id,
      query: query,
      search_id: searchId,
    },
    {
      skip: !query || !searchId,
      refetchOnFocus: false,
    },
  );

  const handleSearch = async (_searchId: string, query: string) => {
    setQuery(query);
    setShowThinkingType(true);
    setSearchId(_searchId);
    setIsProcessing(true);
  };

  const handleReset = () => {
    setQuery("");
    setShowThinkingType(false);
    setThinking("");
    setSearchId("");
    setDocuments(undefined);
    setIsThinkingComplete(false);
    setSnapshotCollectionOutcomes(undefined);
    setIsProcessing(false);
  };

  return (
    <div className="mt-4 flow-root">
      <SnapshotCollectionsOutcomesSearchInput handleSearch={handleSearch} query={query} reset={handleReset} />
      {searchId && (
        <SnapshotCollectionsOutcomesSearchWebsocket
          setDocuments={setDocuments}
          key={searchId}
          setSnapshotCollectionOutcomes={setSnapshotCollectionOutcomes}
          setIsProcessing={setIsProcessing}
          searchId={searchId}
          setThinking={setThinking}
        />
      )}
      <div className="mx-4 my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          {query && (
            <SnapshotCollectionsOutcomesSearchThinking
              setIsThinkingComplete={setIsThinkingComplete}
              thinking={thinking}
              showThinkingType={showThinkingType}
            />
          )}
          {isThinkingComplete && (
            <>
              <motion.div animate={{ opacity: documents ? 1 : 0 }}>
                {!!documents && <SnapshotCollectionsOutcomesSearchDocuments documents={documents} />}
              </motion.div>
              <motion.div animate={{ opacity: snapshotCollectionOutcomes ? 1 : 0 }}>
                {!!snapshotCollectionOutcomes && (
                  <SnapshotCollectionsOutcomesSearchResults snapshotCollectionOutcomes={snapshotCollectionOutcomes} />
                )}
              </motion.div>
            </>
          )}

          {!snapshotCollectionOutcomes && (
            <div className="text-center text-sm text-gray-500">
              {!!query && !isProcessing && "No snapshots found"}
              {!query && "Type to search for snapshots"}
              {isThinkingComplete && query && isProcessing && <Loader />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
